import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { mdiAccountMultiple } from '@mdi/js';

import { TableCell } from 'common/components/Table';
import { Checkbox } from 'common/components/Checkbox';
import { Link } from 'common/components/Link';
import { Avatar } from 'common/components/Avatar';
import { Icon } from 'common/components/Icon';
import { ITrainingGroupListItemFragment } from 'graphql/types';
import { IGlobalDrawerType } from 'common/types';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';

interface IProps {
    group: ITrainingGroupListItemFragment;
    trainingId: string;
    isSelectable?: boolean;
    isSelected?: boolean;
    onSelect?(): void;
}

export const TrainingGroupTableRow = ({
    group,
    trainingId,
    isSelectable,
    isSelected,
    onSelect,
}: IProps) => {
    const [translate] = useTranslation();
    const { openGlobalDrawer, closeGlobalDrawer } = useGlobalDrawer();
    const { id, name, userCount } = group;

    const filtersQuery = useMemo(() => {
        try {
            const encoded = window.btoa(
                JSON.stringify({ groupIds: [{ id, name }] })
            );

            return `?filters=${encoded}`;
        } catch {
            return '';
        }
    }, [id, name]);

    return (
        <TableRow hover>
            {isSelectable && (
                <TableCell padding="checkbox">
                    <Checkbox
                        checked={isSelected}
                        onClick={(e) => {
                            onSelect?.();
                            e.stopPropagation();
                        }}
                    />
                </TableCell>
            )}
            <TableCell
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                    openGlobalDrawer({
                        type: IGlobalDrawerType.Group,
                        itemId: id,
                        props: {
                            onClose: () =>
                                closeGlobalDrawer({
                                    type: IGlobalDrawerType.Group,
                                }),
                        },
                    });
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar bgColor="primary" size={45}>
                        <Icon path={mdiAccountMultiple} size="2.2rem" />
                    </Avatar>
                    <Box sx={{ ml: 2 }}>
                        <Typography variant="body2">{name}</Typography>
                    </Box>
                </Box>
            </TableCell>
            <TableCell>
                <Typography variant="body2">{userCount ?? 0}</Typography>
            </TableCell>
            <TableCell>
                <Link
                    params={{ trainingId }}
                    query={filtersQuery}
                    to="TRAINING_PARTICIPANTS"
                >
                    {translate('trainingGroups.viewProgress')}
                </Link>
            </TableCell>
        </TableRow>
    );
};
